
import { createContext, useContext, useReducer, useEffect, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

type AuthState = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isSessionExpired: boolean;
};

type AuthAction =
  | { type: 'SET_SESSION'; payload: { session: Session | null; user: User | null } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SESSION_EXPIRED'; payload: boolean }
  | { type: 'RESET_STATE' };

const initialState: AuthState = {
  session: null,
  user: null,
  loading: true,
  isSessionExpired: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_SESSION':
      return {
        ...state,
        session: action.payload.session,
        user: action.payload.user,
        isSessionExpired: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'SET_SESSION_EXPIRED':
      return {
        ...state,
        isSessionExpired: action.payload,
      };
    case 'RESET_STATE':
      return {
        ...initialState,
        loading: false,
      };
    default:
      return state;
  }
}

type AuthContextType = AuthState & {
  refreshSession: () => Promise<boolean>;
};

const AuthContext = createContext<AuthContextType>({
  ...initialState,
  refreshSession: async () => false,
});

const SESSION_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // Initial delay of 1 second

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Enhanced refresh session with retry logic and exponential backoff
  const refreshSession = useCallback(async (retryAttempt = 0): Promise<boolean> => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error("Session refresh error:", error);
        
        if (retryAttempt < MAX_RETRY_ATTEMPTS) {
          // Exponential backoff
          const delay = RETRY_DELAY * Math.pow(2, retryAttempt);
          await new Promise(resolve => setTimeout(resolve, delay));
          return refreshSession(retryAttempt + 1);
        }
        
        dispatch({ type: 'SET_SESSION_EXPIRED', payload: true });
        return false;
      }

      if (session) {
        console.log("Session refreshed successfully");
        dispatch({
          type: 'SET_SESSION',
          payload: { session, user: session.user },
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error("Session refresh failed:", error);
      dispatch({ type: 'SET_SESSION_EXPIRED', payload: true });
      return false;
    }
  }, []);

  // Proactive session refresh checker
  const checkAndRefreshSession = useCallback(async (session: Session) => {
    if (!session?.expires_at) return;

    const expiresAt = new Date(session.expires_at * 1000);
    const timeUntilExpiry = expiresAt.getTime() - Date.now();

    if (timeUntilExpiry <= SESSION_REFRESH_THRESHOLD) {
      console.log("Session approaching expiry, refreshing...");
      await refreshSession();
    }
  }, [refreshSession]);

  useEffect(() => {
    let refreshInterval: NodeJS.Timeout;

    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Error getting initial session:", sessionError);
          throw sessionError;
        }

        console.log("Initial session:", session?.user?.email);
        
        if (session) {
          dispatch({
            type: 'SET_SESSION',
            payload: { session, user: session.user },
          });
          await checkAndRefreshSession(session);
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: "There was a problem with authentication. Please try logging in again.",
        });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      console.log("Auth state changed:", event, currentSession?.user?.email);
      
      switch (event) {
        case 'SIGNED_IN':
        case 'TOKEN_REFRESHED':
        case 'USER_UPDATED':
          dispatch({
            type: 'SET_SESSION',
            payload: {
              session: currentSession,
              user: currentSession?.user ?? null,
            },
          });
          break;
        case 'SIGNED_OUT':
          dispatch({ type: 'RESET_STATE' });
          break;
      }
      
      dispatch({ type: 'SET_LOADING', payload: false });

      // Set up periodic session refresh
      if (currentSession) {
        refreshInterval = setInterval(() => {
          checkAndRefreshSession(currentSession);
        }, SESSION_REFRESH_THRESHOLD);
      }
    });

    return () => {
      console.log("Cleaning up auth subscription");
      subscription.unsubscribe();
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [checkAndRefreshSession]);

  return (
    <AuthContext.Provider value={{ 
      ...state,
      refreshSession 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
